import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import ApplicationImportDialog from "@/components/ApplicationImportDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import { api } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Search, Trash2, ShieldAlert, CheckCircle2, Send, Ban, Upload, FileSpreadsheet, ArrowRightCircle } from "lucide-react";

const FIELDS = [
  { k: "ad_soyad", l: "Ad Soyad", req: true },
  { k: "tc_no", l: "TC No" },
  { k: "telefon", l: "Telefon" },
  { k: "referans_telefon", l: "Referans Telefon" },
  { k: "eposta", l: "E-posta" },
  { k: "adres", l: "Adres" },
  { k: "acik_icra", l: "Açık İcra Var mı?", type: "secim" },
  { k: "arac_var", l: "Araç Var mı?", type: "secim" },
  { k: "tasinmaz_var", l: "Taşınmaz Var mı?", type: "secim" },
  { k: "sektor", l: "Çalıştığı Sektör" },
  { k: "kurum_adi", l: "Kurum Adı" },
  { k: "net_maas", l: "En Son Net Maaş", type: "number" },
  { k: "is_yili", l: "Kaç Yıldır Aynı İş Yeri" },
  { k: "telefon_modeli", l: "İstenilen Telefon Modeli" },
];

const APP_COLORS = { "Beklemede": "#8C8377", "Admin Onayında": "#B8933F", "Onaylandı": "#2F6E4E", "Reddedildi": "#A63A3A" };

function Badge({ durum }) {
  const c = APP_COLORS[durum] || "#8C8377";
  return <span className="inline-block px-2 py-0.5 rounded-sm text-xs font-semibold" style={{ backgroundColor: `${c}1a`, color: c }}>{durum}</span>;
}

import CariGorevlerim from "@/components/CariGorevlerim";
import HacizGorevlerim from "@/components/HacizGorevlerim";

export default function Applications({ mineOnly = false }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [durum, setDurum] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/settings/assignment").then((res) => setAgents(res.data.agents || [])).catch(() => {});
    }
  }, [user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (durum) params.durum = durum;
      if (mineOnly && user?.email) params.atanan = user.email;
      const { data } = await api.get("/applications", { params });
      setRows(data);
    } catch (e) {
      toast.error("Başvurular yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [q, durum, mineOnly, user?.email]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/applications/${id}`);
      setDetail(data);
    } catch (e) { toast.error("Detay açılamadı"); }
  };

  const setStatus = async (id, newDurum, adminNotu = "") => {
    try {
      const { data } = await api.post(`/applications/${id}/status`, { durum: newDurum, admin_notu: adminNotu });
      toast.success(`Durum: ${newDurum}`);
      setDetail((d) => (d && d.id === id ? { ...d, durum: data.durum, admin_notu: data.admin_notu, durum_by: data.durum_by, durum_at: data.durum_at } : d));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Durum güncellenemedi");
    }
  };

  const remove = (id) => {
    setConfirm({
      title: "Başvuruyu Sil",
      description: "Bu başvuruyu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.",
      onConfirm: async () => {
        try { await api.delete(`/applications/${id}`); toast.success("Silindi"); setDetail(null); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
        finally { setConfirm(null); }
      },
    });
  };

  const exportExcel = async () => {
    try {
      const res = await api.get("/applications/export/excel", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "on_onay_basvurular.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Excel indirildi");
    } catch (e) { toast.error("Dışa aktarma başarısız"); }
  };

  const toCari = async (app) => {
    if (!window.confirm(`${app.ad_soyad} cari müşteriye aktarılsın mı?`)) return;
    try {
      await api.post(`/applications/${app.id}/to-cari`);
      toast.success("Cari müşteriye aktarıldı");
      setDetail(null);
      navigate(`/cari-musteri/${encodeURIComponent(app.ad_soyad)}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Aktarım başarısız"); }
  };

  const toShipment = async (app) => {
    if (!window.confirm(`${app.ad_soyad} için sevkiyat planına kayıt oluşturulsun mu? (Şehri sevkiyat ekranında gireceksiniz)`)) return;
    try {
      await api.post(`/applications/${app.id}/to-shipment`);
      toast.success("Sevkiyat planına eklendi");
      setDetail(null);
      navigate("/sevkiyat");
    } catch (e) { toast.error(e.response?.data?.detail || "Sevkiyata eklenemedi"); }
  };

  const reassign = async (aid, email) => {
    try { await api.put(`/applications/${aid}/assign`, { atanan: email }); toast.success("Atama güncellendi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Atanamadı"); }
  };

  if (mineOnly && user?.role === "katip") {
    return (
      <Layout>
        <div className="mb-6">
          <div className="kicker mb-1">GÖREV LİSTEM</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Görevlerim</h1>
          <p className="text-sm text-[#8C8377] mt-1">Haciz gündemine alınan ve size düşen dosyalar. Detay için satıra tıklayın.</p>
        </div>
        <HacizGorevlerim />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="kicker mb-1">{mineOnly ? "GÖREV LİSTEM" : "SATIŞ ÖNCESİ SORGU"}</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{mineOnly ? "Görevlerim" : "Ön Onay Başvuruları"}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowImport(true)} data-testid="app-import-button" className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150">
            <Upload className="w-4 h-4 text-[#B8933F]" /> Excel ile Ekle
          </button>
          <button onClick={exportExcel} data-testid="app-export-button" className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150">
            <FileSpreadsheet className="w-4 h-4 text-[#2F6E4E]" /> Excel
          </button>
          <button onClick={() => setShowForm(true)} data-testid="add-application-button" className="flex items-center gap-1.5 px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150">
            <Plus className="w-4 h-4" /> Yeni Başvuru
          </button>
        </div>
      </div>

      {mineOnly && <CariGorevlerim />}
      {mineOnly && (user?.role === "admin") && <HacizGorevlerim />}

      <div className="flex flex-wrap items-end gap-3 mb-5 p-4 bg-white border border-[#E7DECC] rounded-sm">
        <div className="flex-1 min-w-[200px]">
          <label className="kicker block mb-1.5">AD SOYAD ARA</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8C8377]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="app-search" placeholder="Ad soyad..." className="w-full pl-9 pr-3 py-2 border border-[#E7DECC] rounded-sm focus:outline-none focus:border-[#B8933F]" />
          </div>
        </div>
        <div className="w-[200px]">
          <label className="kicker block mb-1.5">DURUM</label>
          <select value={durum} onChange={(e) => setDurum(e.target.value)} data-testid="app-durum-filter" className="w-full px-3 py-2 border border-[#E7DECC] bg-white rounded-sm focus:outline-none focus:border-[#B8933F]">
            <option value="">Tümü</option>
            {APP_STATUSES_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white border border-[#E7DECC] rounded-sm overflow-auto max-h-[calc(100vh-16rem)]" data-testid="applications-table">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-[#F3ECDD]">
            <tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
              <th className="px-3 py-2.5 kicker text-left">Tarih</th>
              <th className="px-3 py-2.5 kicker text-left">Ad Soyad</th>
              <th className="px-3 py-2.5 kicker text-left">Telefon</th>
              <th className="px-3 py-2.5 kicker text-left">Telefon Modeli</th>
              <th className="px-3 py-2.5 kicker text-left">Kaynak</th>
              <th className="px-3 py-2.5 kicker text-left">Risk</th>
              <th className="px-3 py-2.5 kicker text-left">Atanan</th>
              <th className="px-3 py-2.5 kicker text-left">Durum</th>
              <th className="px-3 py-2.5 kicker text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-[#8C8377]">Yükleniyor...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-[#8C8377]" data-testid="app-empty">Başvuru yok.</td></tr>
            ) : (
              rows.map((r, i) => (
                <tr key={r.id} className={`border-b border-[#E7DECC] hover:bg-[#EFE7D6] cursor-pointer transition-colors duration-150 ${r.atanan && r.atanan === user?.email ? "bg-[#B8933F]/[0.06]" : i % 2 === 1 ? "bg-[#F3ECDD]/60" : ""}`} onClick={() => openDetail(r.id)} data-testid={`app-row-${r.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap tabular-nums">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2 whitespace-nowrap font-medium">{r.ad_soyad}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.telefon || "-"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.telefon_modeli || "-"}</td>
                  <td className="px-3 py-2 whitespace-nowrap"><span className="text-xs text-[#8C8377]">{r.source === "web" ? "Web Sitesi" : "Manuel"}</span></td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.risk_match ? (
                      <span className="inline-flex items-center gap-1 text-[#A63A3A] text-xs font-semibold" data-testid={`app-risk-${r.id}`}><ShieldAlert className="w-3.5 h-3.5" /> {r.match_type === "tc" ? "TC Eşleşti" : "İsim Eşleşti"}</span>
                    ) : <span className="text-xs text-[#8C8377]">—</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {isAdmin ? (
                      <select value={r.atanan || ""} onChange={(e) => reassign(r.id, e.target.value)} data-testid={`app-assign-${r.id}`} className="px-2 py-1 border border-[#E7DECC] bg-white rounded-sm text-xs focus:outline-none focus:border-[#B8933F]">
                        <option value="">— Atanmadı —</option>
                        {agents.map((a) => <option key={a} value={a}>{a}</option>)}
                        {r.atanan && !agents.includes(r.atanan) && <option value={r.atanan}>{r.atanan}</option>}
                      </select>
                    ) : (
                      <span className={`text-xs ${r.atanan === user?.email ? "font-semibold text-[#B8933F]" : ""}`}>{r.atanan || "—"}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap"><Badge durum={r.durum} /></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={(e) => { e.stopPropagation(); openDetail(r.id); }} data-testid={`app-detail-${r.id}`} className="px-2 py-1 text-[#B8933F] hover:bg-white border border-[#E7DECC] rounded-sm text-xs font-medium transition-colors duration-150">Detay</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showForm && <ApplicationForm onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} />}
      {showImport && <ApplicationImportDialog onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />}
      {detail && <ApplicationDetail app={detail} isAdmin={user?.role === "admin"} onClose={() => setDetail(null)} onStatus={setStatus} onDelete={remove} onToCari={toCari} onToShipment={toShipment} />}
      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => { if (!o) setConfirm(null); }}
        title={confirm?.title}
        description={confirm?.description}
        onConfirm={confirm?.onConfirm}
      />
    </Layout>
  );
}

const APP_STATUSES_LIST = ["Beklemede", "Admin Onayında", "Onaylandı", "Reddedildi"];

function ApplicationForm({ onClose, onDone }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.ad_soyad) { toast.error("Ad Soyad zorunlu"); return; }
    setSaving(true);
    try {
      const payload = { ...form, net_maas: Number(form.net_maas) || 0 };
      await api.post("/applications", payload);
      toast.success("Başvuru eklendi");
      onDone();
    } catch (e) { toast.error("Başvuru eklenemedi"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white border border-[#E7DECC] rounded-sm w-full max-w-2xl my-8" data-testid="application-form">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E7DECC]">
          <h3 className="font-heading text-xl font-bold tracking-tight">Yeni Ön Onay Başvurusu</h3>
          <button onClick={onClose} data-testid="app-form-close" aria-label="Kapat" className="text-[#8C8377] hover:text-[#26231E]"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELDS.map((f) => (
              <div key={f.k} className={f.k === "adres" ? "md:col-span-2" : ""}>
                <label className="kicker block mb-1.5">{f.l}{f.req ? " *" : ""}</label>
                {f.type === "secim" ? (
                  <select value={form[f.k] || ""} onChange={(e) => change(f.k, e.target.value)} data-testid={`app-field-${f.k}`} className="w-full px-3 py-2 border border-[#E7DECC] bg-white rounded-sm focus:outline-none focus:border-[#B8933F]">
                    <option value="">Seçin</option>
                    <option value="Var">Var</option>
                    <option value="Yok">Yok</option>
                  </select>
                ) : (
                  <input type={f.type === "number" ? "number" : "text"} value={form[f.k] || ""} onChange={(e) => change(f.k, e.target.value)} required={f.req} data-testid={`app-field-${f.k}`} className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm focus:outline-none focus:border-[#B8933F]" />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-[#E7DECC] rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150">İptal</button>
            <button type="submit" disabled={saving} data-testid="app-form-save" className="px-6 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150 disabled:opacity-60">{saving ? "Kaydediliyor..." : "Kaydet"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ApplicationDetail({ app, isAdmin, onClose, onStatus, onDelete, onToCari, onToShipment }) {
  const risk = app.risk || {};
  const hasHistory = (risk.icra_kayit || 0) > 0 || (risk.cari_kayit || 0) > 0;
  const [note, setNote] = useState(app.admin_notu || "");
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white border border-[#E7DECC] rounded-sm w-full max-w-2xl my-8" data-testid="application-detail">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E7DECC]">
          <div>
            <div className="kicker mb-0.5">BAŞVURU DETAYI</div>
            <h3 className="font-heading text-xl font-bold tracking-tight">{app.ad_soyad}</h3>
          </div>
          <div className="flex items-center gap-3">
            <Badge durum={app.durum} />
            <button onClick={onClose} data-testid="app-detail-close" aria-label="Kapat" className="text-[#8C8377] hover:text-[#26231E]"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-6">
          <div className={`mb-5 p-4 rounded-sm border ${hasHistory ? "border-[#A63A3A]/30 bg-[#A63A3A]/5" : "border-[#2F6E4E]/30 bg-[#2F6E4E]/5"}`} data-testid="app-risk-box">
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert className={`w-4 h-4 ${hasHistory ? "text-[#A63A3A]" : "text-[#2F6E4E]"}`} />
              <span className="font-heading font-bold text-sm">{hasHistory ? "Geçmiş Kayıt Bulundu" : "Geçmiş Kayıt Yok"}</span>
              {risk.tc_eslesme && (
                <span className="inline-block px-2 py-0.5 rounded-sm text-xs font-semibold bg-[#A63A3A]/10 text-[#A63A3A]" data-testid="app-tc-match-badge">TC No ile eşleşti</span>
              )}
              {(risk.onceki_basvuru || 0) > 0 && (
                <span className="inline-block px-2 py-0.5 rounded-sm text-xs font-semibold bg-[#B8933F]/10 text-[#B8933F]" data-testid="app-prev-app-badge">{risk.onceki_basvuru} önceki başvuru</span>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><div className="kicker mb-0.5">İcra Kaydı</div><div className="font-semibold tabular-nums">{risk.icra_kayit || 0}</div></div>
              <div><div className="kicker mb-0.5">İcra Net Risk</div><div className="font-semibold tabular-nums text-[#A63A3A]">{fmt(risk.icra_net_risk)}</div></div>
              <div><div className="kicker mb-0.5">Cari Kaydı</div><div className="font-semibold tabular-nums">{risk.cari_kayit || 0}</div></div>
              <div><div className="kicker mb-0.5">Cari Kalan</div><div className="font-semibold tabular-nums text-[#A63A3A]">{fmt(risk.cari_kalan)}</div></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 mb-6">
            {FIELDS.map((f) => (
              <div key={f.k} className={`flex justify-between gap-3 border-b border-[#EFE7D6] pb-1.5 ${f.k === "adres" ? "md:col-span-2" : ""}`}>
                <span className="kicker">{f.l}</span>
                <span className="text-sm font-medium text-right">{f.type === "number" ? fmt(app[f.k]) : (app[f.k] || "-")}</span>
              </div>
            ))}
          </div>

          <div className="mb-5" data-testid="app-note-section">
            <label className="kicker block mb-1.5">ADMIN AÇIKLAMA NOTU</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="app-admin-note" placeholder="Örn: 10.000 TL peşinat ile uygundur" className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm focus:outline-none focus:border-[#B8933F]" />
            {app.admin_notu && (
              <div className="mt-2 p-2 rounded-sm bg-[#F3ECDD] border border-[#E7DECC] text-sm"><span className="kicker">KAYITLI NOT:</span> {app.admin_notu}</div>
            )}
            {app.durum_by && (
              <div className="text-xs text-[#8C8377] mt-1">Son işlem: {app.durum_by}{app.durum_at ? ` — ${fmtDate(app.durum_at)}` : ""}</div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            {isAdmin ? (
              <button onClick={() => onDelete(app.id)} data-testid="app-delete" className="flex items-center gap-1.5 px-3 py-2 text-[#A63A3A] border border-[#E7DECC] rounded-sm text-sm font-medium hover:bg-[#A63A3A]/5 transition-colors duration-150"><Trash2 className="w-4 h-4" /> Sil</button>
            ) : <span />}
            <div className="flex items-center gap-2">
              {app.durum === "Beklemede" && (
                <button onClick={() => onStatus(app.id, "Admin Onayında", note)} data-testid="app-send-approval" className="flex items-center gap-1.5 px-4 py-2 bg-[#B8933F] hover:bg-[#e08600] text-white rounded-sm text-sm font-medium transition-colors duration-150"><Send className="w-4 h-4" /> Admin Onayına Gönder</button>
              )}
              {app.durum !== "Reddedildi" && app.durum !== "Onaylandı" && (
                <button onClick={() => onStatus(app.id, "Reddedildi", note)} data-testid="app-reject" className="flex items-center gap-1.5 px-4 py-2 border border-[#E7DECC] text-[#A63A3A] rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150"><Ban className="w-4 h-4" /> Reddet</button>
              )}
              {app.durum === "Admin Onayında" && (
                <button onClick={() => onStatus(app.id, "Onaylandı", note)} disabled={!isAdmin} title={isAdmin ? "" : "Yalnızca yönetici onaylayabilir"} data-testid="app-approve" className="flex items-center gap-1.5 px-4 py-2 bg-[#2F6E4E] hover:bg-[#2aa347] text-white rounded-sm text-sm font-medium transition-colors duration-150 disabled:opacity-40"><CheckCircle2 className="w-4 h-4" /> Onayla (Yönetici)</button>
              )}
              {app.durum === "Onaylandı" && (
                <button onClick={() => onToShipment(app)} data-testid="app-to-shipment" className="flex items-center gap-1.5 px-4 py-2 bg-[#B8933F] hover:bg-[#e08600] text-white rounded-sm text-sm font-medium transition-colors duration-150"><Send className="w-4 h-4" /> Sevkiyata Ekle</button>
              )}
              {app.durum === "Onaylandı" && (
                <button onClick={() => onToCari(app)} data-testid="app-to-cari" className="flex items-center gap-1.5 px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150"><ArrowRightCircle className="w-4 h-4" /> Cari'ye Aktar</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
